import React, { useMemo, useState } from "react";
import "../promotersPages.scss";
import AuthClient from "../../../api/AuthClient";
import { PartyModel } from "../../../types";
import RefreshButton from "../../RefreshToUpdateData/RefreshToUpdateButton";
import AcceptedGroupDetail from "./AcceptedGroupDetail";
import { useAcceptedGroups } from "../../../hooks/useServerDataQueries";

interface Props {
  authClient: AuthClient;
}

const toLocalIsoDate = (value: Date | string): string => {
  const date = value instanceof Date ? value : new Date(value);
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const todayIso = (): string => toLocalIsoDate(new Date());

const normalizeCity = (city: string | undefined | null): string =>
  (city ?? "").trim().toLowerCase();

const Accepted: React.FC<Props> = ({ authClient }) => {
  const {
    data: acceptedGroups = [],
    isFetching,
    isLoading,
    isError,
    refetch,
  } = useAcceptedGroups();
  const requeststate = isFetching || isLoading ? "loading" : isError ? "error" : "idle";

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedCity, setSelectedCity] = useState<string>("");
  const [isDateFilterEnabled, setIsDateFilterEnabled] = useState<boolean>(true);
  const [selectedDate, setSelectedDate] = useState<string>(todayIso);

  const cityOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const g of acceptedGroups) {
      const raw = g.party?.city ?? "";
      const key = normalizeCity(raw);
      if (key && !seen.has(key)) seen.set(key, raw.trim());
    }
    return Array.from(seen.values()).sort((a, b) => a.localeCompare(b));
  }, [acceptedGroups]);

  const hasActiveFilters = selectedCity !== "" || isDateFilterEnabled;

  const filteredGroups: PartyModel[] = acceptedGroups.filter((group: PartyModel) => {
    if (
      selectedCity !== "" &&
      normalizeCity(group.party?.city) !== normalizeCity(selectedCity)
    ) {
      return false;
    }
    if (
      isDateFilterEnabled &&
      toLocalIsoDate(group.party.partyDate) !== selectedDate
    ) {
      return false;
    }
    return true;
  });

  const handleRefreshClick = () => {
    void refetch();
  };

  return (
    <>
      <div className="mb-3 refreshRequestContainer acceptedTopBar">
        <button
          type="button"
          className="btn btn-sm btn-outline-light acceptedFilterToggle"
          onClick={() => setIsFilterOpen((open) => !open)}
          aria-expanded={isFilterOpen}
        >
          Filter{hasActiveFilters ? " •" : ""}
        </button>
        <RefreshButton requestState={requeststate} onClickHandler={handleRefreshClick} />
      </div>

      {isFilterOpen ? (
        <div className="mb-3 acceptedFilterPanel">
          <div className="input-group mb-2">
            <select
              className="form-control shadow-none"
              aria-label="City"
              value={selectedCity}
              onChange={(event) => setSelectedCity(event.target.value)}
            >
              <option value="">All cities</option>
              {cityOptions.map((city) => (
                <option key={city} value={city}>
                  {city}
                </option>
              ))}
            </select>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <label className="form-check d-flex align-items-center mb-0 gap-2">
              <input
                type="checkbox"
                className="form-check-input m-0"
                checked={isDateFilterEnabled}
                onChange={(event) => setIsDateFilterEnabled(event.target.checked)}
              />
             <span className="form-check-label text-white">Filter by date</span>
            </label>
            <input
              type="date"
              className="form-control shadow-none acceptedFilterDate"
              value={selectedDate}
              onChange={(event) => setSelectedDate(event.target.value)}
              disabled={!isDateFilterEnabled}
              aria-label="Date filter"
            />
          </div>
        </div>
      ) : null}

      {filteredGroups.length > 0 ? (
        <div>
          {filteredGroups.map((group: PartyModel) => (
            <AcceptedGroupDetail key={group.party.id} group={group} />
          ))}
        </div>
      ) : acceptedGroups.length === 0 ? (
        <div className="m-4 p-4 text-white text-center">No groups</div>
      ) : (
        <div className="m-4 p-4 text-white text-center">
          No accepted groups match these filters
        </div>
      )}
    </>
  );
};

export default Accepted;
